// Layout.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLinkedin, FaXTwitter, FaSun, FaMoon } from "react-icons/fa6";
import "./style/header.css";

import userData from "./data/userData.json";
import ParticleWave from "./components/ParticleWave";
import Logo from "./components/Logo";

/* ==============================
   TERMINAL TYPEWRITER (LOOPING)
   ============================== */
const TerminalName = ({ text }) => {
  const typingSpeed = 180;
  const deletingSpeed = 120;
  const pauseAfterTyping = 6500;
  const pauseAfterDeleting = 500;

  const [output, setOutput] = useState("");
  const [index, setIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cursor, setCursor] = useState(true);

  useEffect(() => {
    let timer;

    if (!isDeleting && index < text.length) {
      timer = setTimeout(() => {
        setOutput(prev => prev + text[index]);
        setIndex(i => i + 1);
      }, typingSpeed);
    } else if (!isDeleting && index === text.length) {
      timer = setTimeout(() => setIsDeleting(true), pauseAfterTyping);
    } else if (isDeleting && index > 0) {
      timer = setTimeout(() => {
        setOutput(prev => prev.slice(0, -1));
        setIndex(i => i - 1);
      }, deletingSpeed);
    } else if (isDeleting && index === 0) {
      timer = setTimeout(() => setIsDeleting(false), pauseAfterDeleting);
    }

    return () => clearTimeout(timer);
  }, [index, isDeleting, text]);

  useEffect(() => {
    const blink = setInterval(() => setCursor(v => !v), 600);
    return () => clearInterval(blink);
  }, []);

  return (
    <span style={{ color: "var(--accent)", fontFamily: "monospace" }}>
      {output}
      <span style={{ opacity: cursor ? 1 : 0 }}>|</span>
    </span>
  );
};

/* ==============================
   LAYOUT
   ============================== */
const NAV_ITEMS = [
  { label: "HOME", path: "/" },
  { label: "BLOG", path: "/blog" },
  { label: "PROJECTS", path: "/projects" },
  { label: "ABOUT", path: "/about" },
];

function getInitialTheme() {
  // The inline bootstrap in public/index.html has already made this decision
  // and written it to the root element — it has to, because it also preloads
  // the theme's background image before the bundle loads. Reading it back is
  // what keeps the two from ever disagreeing; the fallback below only matters
  // if that script did not run (e.g. the dev server's template, or tests).
  const applied = document.documentElement.getAttribute("data-theme");
  if (applied === "light" || applied === "dark") return applied;

  const stored = window.localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem("theme", theme);
  }, [theme]);

  // colors are driven from userData.json so they can be tweaked without touching CSS.
  //
  // These land as inline styles on <html>, which outrank both :root and
  // [data-theme="light"] in index.css — so anything set here has to carry its
  // own light/dark answer rather than leaning on the stylesheet for one.
  // --accent-secondary is the case that forced it: the original #006400 reads
  // at 2.82:1 on black, well under the 4.5:1 .featured-title needs, and no
  // single green clears 4.5:1 against black *and* white with any margin.
  useEffect(() => {
    const root = document.documentElement.style;
    const { primary, secondary } = userData.colors ?? {};

    if (primary) root.setProperty("--accent", primary);

    // Accepts either a plain string or a { dark, light } pair.
    const secondaryForTheme =
      typeof secondary === "string" ? secondary : secondary?.[theme];
    if (secondaryForTheme) root.setProperty("--accent-secondary", secondaryForTheme);
  }, [theme]);

  // The Ko-fi widget script used to run here and inject "Support" as a filled
  // pill with its own font, stylesheet and wiggle animation. It is gone: it
  // arrived a beat after first paint, and that late insertion reflowed the
  // header — which is what made the contact row look like it was part of the
  // title's typing animation. "Support" is a plain link in the markup now, so
  // it is laid out on the first paint and never moves again.

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <div className="layout">
      {/* ================= 3D PARTICLE WAVE BACKGROUND ================= */}
      <ParticleWave theme={theme} />

      {/* ================= HEADER ================= */}
      <header className="header">
        <div className="brand">
          <Logo className="brand__mark" shadow={false} aria-hidden="true" />
          <h1>
            C:\Users\&gt; <TerminalName text={userData.terminalName} />
          </h1>
        </div>

        <h2 className="tagline">
          {userData.tagline}
        </h2>

        {/* The third row, under the tagline. Static markup on purpose — this
            sits below an element that retypes itself on a loop, so anything
            here that arrives late or changes width would read as part of
            that animation. aria-label carries the real address, and has to
            start with the visible word "Contact": WCAG 2.5.3 (and the axe
            "label-content-name-mismatch" rule) require the accessible name to
            contain the visible text, so that someone using voice control can
            say what they can see. */}
        <div className="contact" role="group" aria-label="Contact and support">
          <a
            className="contact__link"
            href={`mailto:${userData.email}`}
            aria-label={`Contact — email ${userData.email}`}
          >
            Contact
          </a>
          <span className="contact__sep" aria-hidden="true">|</span>
          <a
            className="contact__link"
            href={userData.socials.kofi}
            target="_blank"
            rel="noopener noreferrer"
          >
            Support
          </a>
        </div>
      </header>

      {/* ================= NAV ================= */}
      <nav className="nav">
        {NAV_ITEMS.map(item => (
          <button
            key={item.path}
            className={isActive(item.path) ? "active" : ""}
            aria-current={isActive(item.path) ? "page" : undefined}
            onClick={() => navigate(item.path)}
          >
            [ {item.label} ]
          </button>
        ))}

        <button
          className={`theme-switch${theme === "light" ? " is-light" : ""}`}
          onClick={() => setTheme(t => (t === "dark" ? "light" : "dark"))}
          role="switch"
          aria-checked={theme === "light"}
          aria-label="Toggle light/dark mode"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <span className="theme-switch__track">
            <span className="theme-switch__thumb">
              {theme === "dark" ? <FaMoon /> : <FaSun />}
            </span>
          </span>
        </button>
      </nav>

      {/* ================= MAIN ================= */}
      <main className="main">
        <section className="page-content">
          {children}
        </section>
      </main>

      {/* ================= FOOTER ================= */}
      <footer>
        <p>{userData.footer.copyright}</p>
        <p>{userData.footer.note}</p>

        <div className="social-links">
          <a
            href={userData.socials.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin className="social-icon" />
          </a>

          <a
            href={userData.socials.x}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X"
          >
            <FaXTwitter className="social-icon" />
          </a>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
